import { HotelListRequest, HotelListResponse } from 'proto/Api/Hotel/HotelList';
import { ResponseStatus } from 'proto/Api/ResponseStatus';
import { HotelList } from 'proto/Hotel/Hotel';

import { HotelListView } from '../../../component/Accommodation/List/__partials/List/types';
import { ReducerState } from '../types';

export const hasListEntries = (hotelList: HotelList | undefined) =>
    !!hotelList?.itemOrder.length;

const getPollUrlFromResponse = (
    response: HotelListResponse,
    currentPollUrl: string
) => {
    if (currentPollUrl.length) {
        return currentPollUrl;
    }
    return response.status === ResponseStatus.Pending &&
        response.hotelList?.pollUrl.length
        ? response.hotelList.pollUrl
        : '';
};

const getViewFromResponse = (
    response: HotelListResponse,
    currentView: HotelListView,
    isLoading = false
): HotelListView => {
    if (isLoading) {
        return HotelListView.FakeTiles;
    }
    if (response.status === ResponseStatus.Success) {
        return HotelListView.ResultList;
    }
    if (
        response.status === ResponseStatus.Empty ||
        response.status === ResponseStatus.Timeout
    ) {
        return hasListEntries(response.hotelList) ||
            hasListEntries(response.alternativeHotelList)
            ? HotelListView.ResultList
            : HotelListView.NoResult;
    }
    if (response.status === ResponseStatus.Pending) {
        return hasListEntries(response.hotelList) ||
            hasListEntries(response.alternativeHotelList)
            ? HotelListView.ResultList
            : currentView;
    }
    const legacyFakeTiles = document.getElementById(
        'js-hotel-fake-tile-wrapper'
    );
    legacyFakeTiles?.remove();

    return HotelListView.OutOfService;
};

const getStatusForResponse = (
    defaultStatus: ResponseStatus,
    response: HotelListResponse,
    isPending: boolean,
    nonEmptyStatus = ResponseStatus.Success
) => {
    if (isPending) {
        return ResponseStatus.Pending;
    } else if (
        hasListEntries(response.hotelList) ||
        hasListEntries(response.alternativeHotelList)
    ) {
        return nonEmptyStatus;
    }
    return defaultStatus;
};

export const getStateForPollingFailure = (
    currentState: ReducerState,
    pollingStatus: ResponseStatus,
    nonEmptyStatus = ResponseStatus.Success
): ReducerState => {
    const responseData = {
        ...currentState.responseData,
        status: getStatusForResponse(
            pollingStatus,
            currentState.responseData,
            !!currentState.pendingRequest,
            nonEmptyStatus
        ),
    };

    return {
        ...currentState,
        responseData,
        view: getViewFromResponse(responseData, currentState.view),
        pollingFinished: true,
        isLoading: false,
        timeoutNotified: false,
    };
};

export const getUpdatedStateForAccommodationList = (
    response: HotelListResponse,
    currentState: ReducerState
): ReducerState => {
    const pollingStarted = !!currentState.pollingUrl.length;
    const pendingRequest =
        response.status === ResponseStatus.Success
            ? undefined
            : currentState.pendingRequest;

    const isLoading =
        response.status !== ResponseStatus.Pending ||
        response.hotelList?.itemOrder.length
            ? false
            : currentState.isLoading;

    const responseData = {
        ...response,
        hotelList:
            response.hotelList?.itemOrder.length ||
            !isLoading ||
            (!pollingStarted && !currentState.requestData.isInitialRequest)
                ? response.hotelList
                : currentState.responseData.hotelList,
        travelTickerList:
            response.travelTickerList ??
            currentState.responseData.travelTickerList,
    };
    responseData.status = currentState.timeoutNotified
        ? ResponseStatus.Timeout
        : getStatusForResponse(
              responseData.status,
              responseData,
              !!pendingRequest ||
                  (pollingStarted && !currentState.pollingFinished),
              pollingStarted ? ResponseStatus.Success : responseData.status
          );

    const view = getViewFromResponse(
        responseData,
        currentState.view,
        isLoading
    );

    if (
        responseData.status === ResponseStatus.Pending &&
        !responseData.hotelList?.itemOrder.length &&
        currentState.responseData.status === ResponseStatus.Error
    ) {
        responseData.status = ResponseStatus.Error;
    }

    return {
        ...currentState,
        responseData,
        view,
        isLoading,
        // set pollingUrl to start polling
        pollingUrl: getPollUrlFromResponse(response, currentState.pollingUrl),
        // on pending request present, list call will be still running, otherwise set to false
        listCallRunning: !!pendingRequest,
        // fire pending request if present
        requestData: pendingRequest ?? currentState.requestData,
        // clear pending request
        pendingRequest: undefined,
        timeoutNotified: false,
    };
};

export const triggerNextAccommodationListFetch = (
    request: HotelListRequest,
    currentState: ReducerState
): ReducerState => {
    if (currentState.listCallRunning) {
        return {
            ...currentState,
            // simply set pending request (will be fired directly after current call finished)
            pendingRequest: request,
        };
    }

    return {
        ...currentState,
        // new requestData triggers fetchHotelList call
        requestData: request,
        // after this there will be definitely a running list call
        listCallRunning: true,
    };
};
